/** Authenticated bounded search: RLS and fresh navigation grants both gate every result. */
import { getSupabaseClient } from '../utils/supabase/client';
import { WorkspaceNavigationService, type WorkspaceNavigationBatch } from './WorkspaceNavigationService';
import { buildPrettyProjectPath, buildPrettyTaskPath } from '../utils/prettyRoutes';
import { currentGlobalSearchCandidateGeneration, matchesGlobalSearchCandidate, rememberGlobalSearchCandidates, storeGlobalSearchCandidates } from './globalSearchCandidates';
export type GlobalSearchType = 'project' | 'task';
export interface GlobalSearchResult { id: string; type: GlobalSearchType; label: string; description?: string; projectId?: string; workspaceId?: string }
export interface GlobalSearchRequest { actorId: string; workspaceId: string; query: string; signal?: AbortSignal; onProgress?: (page: GlobalSearchPage) => void }
export interface GlobalSearchPage { results: GlobalSearchResult[]; cursors: Record<GlobalSearchType, number> & { backend?: 'rpc' | 'legacy' }; hasMore: Record<GlobalSearchType, boolean>; unavailable: GlobalSearchType[] }
interface Scope { actorId: string; workspaceId: string; crossWorkspace: boolean; role: string }
interface Project { id: string; name: string; client_id: string; workspace_id: string; project_number: number | null; contract_number: string | null }
interface Task { id: string; title: string; status: string; project_id: string | null; created_by: string }
const PAGE_SIZE = 20;
const SCAN_PAGES = 4;
const PROJECT_SELECT = 'id,name,client_id,workspace_id,project_number,contract_number';
const TYPES: GlobalSearchType[] = ['project', 'task'];
const fail = () => new Error('Search permissions could not be verified.');
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const rows = <T,>(value: unknown): T[] => Array.isArray(value) ? value as T[] : [];
const checkAbort = (signal?: AbortSignal) => { if (signal?.aborted) throw new DOMException('Aborted', 'AbortError'); };
// Quoted PostgREST values keep punctuation literal; SQL wildcard characters are escaped separately.
export function literalSearchPattern(value: string): string { return `%${value.replace(/[\\%_]/g, '\\$&')}%`; }
const quoted = (value: string) => `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
async function actorMatches(actorId: string) {
  const { data, error } = await getSupabaseClient().auth.getSession();
  if (error || data.session?.user.id !== actorId) throw fail();
}
async function resolveScope(request: GlobalSearchRequest): Promise<Scope> {
  checkAbort(request.signal);
  await actorMatches(request.actorId);
  const client = getSupabaseClient();
  const [account, workspace, membership] = await Promise.all([
    client.from('users').select('id,role,status').eq('id', request.actorId).abortSignal(request.signal!).maybeSingle(),
    client.from('workspaces').select('id,type,status,is_active').eq('id', request.workspaceId).abortSignal(request.signal!).maybeSingle(),
    client.from('workspace_memberships').select('user_id,workspace_id,status').eq('workspace_id', request.workspaceId).eq('user_id', request.actorId).eq('status', 'active').abortSignal(request.signal!).maybeSingle(),
  ]);
  if (account.error || workspace.error || membership.error || !account.data || !workspace.data || !membership.data
    || account.data.id !== request.actorId || workspace.data.id !== request.workspaceId
    || membership.data.user_id !== request.actorId || membership.data.workspace_id !== request.workspaceId
    || typeof account.data.role !== 'string' || account.data.status !== 'active' || workspace.data.status !== 'active' || workspace.data.is_active !== true
    || !['internal', 'organisation'].includes(workspace.data.type)) throw fail();
  const role = String(account.data.role);
  let crossWorkspace = false;
  if (workspace.data.type === 'internal' && ['super_admin', 'internal', 'staff'].includes(role)) crossWorkspace = true;
  await actorMatches(request.actorId);
  checkAbort(request.signal);
  return { actorId: request.actorId, workspaceId: request.workspaceId, crossWorkspace, role };
}
function hasItem(items: unknown, ids: string[]) {
  if (!Array.isArray(items) || items.some(item => !record(item) || typeof item.id !== 'string')) throw fail();
  return items.some(item => ids.includes(item.id));
}
async function checkedNavigation(scope: Scope, workspaceId: string, projectIds: string[]): Promise<WorkspaceNavigationBatch> {
  const result = await WorkspaceNavigationService.batch(workspaceId, projectIds);
  if (!result || result.userId !== scope.actorId || result.workspaceId !== workspaceId || !Array.isArray(result.projects)) throw fail();
  hasItem(result.main, []);
  const returnedIds = new Set<string>();
  for (const project of result.projects) {
    if (!record(project) || typeof project.projectId !== 'string' || !projectIds.includes(project.projectId) || returnedIds.has(project.projectId)) throw fail();
    returnedIds.add(project.projectId);
    hasItem(project.items, []);
  }
  return result;
}
async function navigation(scope: Scope, projects: Project[]): Promise<WorkspaceNavigationBatch> {
  const selectedIds = projects.filter(project => project.workspace_id === scope.workspaceId).map(project => project.id);
  if (!scope.crossWorkspace) return checkedNavigation(scope, scope.workspaceId, selectedIds);
  // Groups are bounded by the candidate page; do not fan out per record or consult a cached rail.
  const groups = new Map<string, string[]>();
  for (const project of projects) {
    if (project.workspace_id === scope.workspaceId) continue;
    groups.set(project.workspace_id, [...(groups.get(project.workspace_id) || []), project.id]);
  }
  // The same selected and resource checks remain mandatory; independent RPCs can run together.
  const [selected, ...resourceNavigation] = await Promise.all([
    checkedNavigation(scope, scope.workspaceId, selectedIds),
    ...[...groups].map(([workspaceId, ids]) => checkedNavigation(scope, workspaceId, ids)),
  ]);
  return { ...selected, projects: [...selected.projects, ...resourceNavigation.flatMap(result => result.projects)] };
}
function permits(nav: WorkspaceNavigationBatch, type: GlobalSearchType, projectId?: string | null) {
  if (type === 'task' && !hasItem(nav.main, ['calendar'])) return false;
  if (!projectId) return type === 'task';
  const project = nav.projects.find(item => item.projectId === projectId);
  if (!project) return false;
  if (type === 'project') return hasItem(nav.main, ['projects']) && project.items.length > 0;
  return hasItem(project.items, ['tasks']);
}
async function readProjects(ids: string[], scope: Scope, signal?: AbortSignal): Promise<Project[]> {
  if (!ids.length) return [];
  let query = getSupabaseClient().from('projects').select(PROJECT_SELECT).in('id', [...new Set(ids)]).eq('archive_status', 'active').order('id');
  if (!scope.crossWorkspace) query = query.eq('workspace_id', scope.workspaceId);
  const response = await query.abortSignal(signal!);
  if (response.error) throw fail();
  return rows<Project>(response.data).filter(project => !!project.workspace_id && (scope.crossWorkspace || project.workspace_id === scope.workspaceId));
}
async function customerNames(projects: Project[], signal?: AbortSignal): Promise<Map<string, string>> {
  if (!projects.length) return new Map();
  const response = await getSupabaseClient().from('companies').select('id,name').in('id', [...new Set(projects.map(project => project.client_id))]).abortSignal(signal!);
  if (response.error) throw fail();
  return new Map(rows<{ id: string; name: string }>(response.data).filter(item => !!item.name).map(item => [item.id, item.name]));
}
async function authorizeCandidates(type: GlobalSearchType, candidates: Array<Project | Task>, scope: Scope, signal?: AbortSignal, includeNames = false) {
  const projectIds = candidates.flatMap(candidate => {
    if (type === 'project') return [candidate.id];
    const item = candidate as Task;
    return item.project_id ? [item.project_id] : [];
  });
  const projects = await readProjects(projectIds, scope, signal);
  const [nav, names] = await Promise.all([navigation(scope, projects), includeNames ? customerNames(projects, signal) : Promise.resolve(new Map<string, string>())]);
  const allowed = candidates.filter(candidate => {
    if (type === 'project') return projects.some(project => project.id === candidate.id) && permits(nav, type, candidate.id);
    const item = candidate as Task;
    const projectId = item.project_id;
    if (!projectId) return type === 'task' && (item as Task).created_by === scope.actorId && permits(nav, type);
    return projects.some(project => project.id === projectId) && permits(nav, type, projectId);
  });
  return { allowed, projects, names };
}
async function candidatePage(type: GlobalSearchType, request: GlobalSearchRequest, scope: Scope, offset: number, id?: string | string[]) {
  const client = getSupabaseClient();
  const pattern = literalSearchPattern(request.query.trim().slice(0, 200));
  if (type === 'project') {
    const generation = currentGlobalSearchCandidateGeneration();
    let query = client.from('projects').select(PROJECT_SELECT).eq('archive_status', 'active');
    if (!scope.crossWorkspace) query = query.eq('workspace_id', scope.workspaceId);
    if (id) query = Array.isArray(id) ? query.in('id', id) : query.eq('id', id);
    else query = query.or(`name.ilike.${quoted(pattern)},contract_number.ilike.${quoted(pattern)}${/^\d{1,9}$/.test(request.query.trim()) ? `,project_number.eq.${Number(request.query.trim())}` : ''}`);
    const response = await query.order('name').order('id').range(offset, offset + PAGE_SIZE - 1).abortSignal(request.signal!);
    if (response.error) throw fail();
    const candidates = rows<Project>(response.data);
    if (!id) {
      checkAbort(request.signal);
      // RLS-read metadata enriches ID hints only; presentation and final grants remain unchanged.
      const hints = candidates.filter(project => !!project.workspace_id && (scope.crossWorkspace || project.workspace_id === scope.workspaceId))
        .map(({ id, name, contract_number, project_number, workspace_id }) => ({ id, name, contract_number, project_number, workspace_id }));
      rememberGlobalSearchCandidates(request.actorId, request.workspaceId, hints, generation);
    }
    return candidates;
  }
  {
    // Inner project join confines customer tasks before names leave Postgres. Personal tasks use an owner-bound branch.
    const make = (personal: boolean) => {
      let query = client.from('tasks').select(personal ? 'id,title,status,project_id,created_by' : 'id,title,status,project_id,created_by,projects!tasks_project_id_fkey!inner(id,workspace_id,archive_status)').neq('status', 'cancelled');
      if (personal) query = query.is('project_id', null).eq('created_by', scope.actorId);
      else { query = query.eq('projects.archive_status', 'active'); if (!scope.crossWorkspace) query = query.eq('projects.workspace_id', scope.workspaceId); }
      if (id) query = Array.isArray(id) ? query.in('id', id) : query.eq('id', id); else query = query.ilike('title', pattern);
      return query.order('title').order('id').range(offset, offset + PAGE_SIZE - 1).abortSignal(request.signal!);
    };
    const [project, personal] = await Promise.all([make(false), make(true)]);
    if (project.error || personal.error) throw fail();
    return [...rows<Task>(project.data), ...rows<Task>(personal.data)];
  }

}
async function presentCandidates(type: GlobalSearchType, candidates: Array<Project | Task>, scope: Scope, signal?: AbortSignal): Promise<GlobalSearchResult[]> {
  const { allowed, projects, names } = await authorizeCandidates(type, candidates, scope, signal, true);
  return allowed.flatMap(candidate => {
    const projectId = type === 'project' ? candidate.id : (candidate as Task).project_id;
    const project = projects.find(item => item.id === projectId);
    if (project && !names.has(project.client_id)) return [];
    return [{ id: candidate.id, type, label: type === 'task' ? (candidate as Task).title : (candidate as Project).name,
      projectId: project?.id, workspaceId: project?.workspace_id,
      description: type === 'project' ? [project?.project_number, project ? names.get(project.client_id) : ''].filter(Boolean).join(' · ') : project?.name || 'Personal task' }];
  });
}
async function revalidateCarried(request: GlobalSearchRequest, scope: Scope, carried: GlobalSearchResult[]): Promise<GlobalSearchResult[]> {
  const fresh: GlobalSearchResult[] = [];
  for (const type of TYPES) {
    const ids = [...new Set(carried.filter(result => result.type === type).map(result => result.id))];
    // Every previous result is re-read in small batches. A More action cannot preserve revoked labels.
    for (let offset = 0; offset < ids.length; offset += PAGE_SIZE) {
      checkAbort(request.signal);
      const candidates = await candidatePage(type, request, scope, 0, ids.slice(offset, offset + PAGE_SIZE));
      fresh.push(...await presentCandidates(type, candidates, scope, request.signal));
    }
  }
  return fresh;
}
async function sourcePage(type: GlobalSearchType, request: GlobalSearchRequest, scope: Scope, offset: number) {
  const results: GlobalSearchResult[] = [];
  let more = false;
  let cursor = offset;
  for (let scanned = 0; scanned < SCAN_PAGES; scanned++) {
    checkAbort(request.signal);
    const candidates = await candidatePage(type, request, scope, cursor);
    cursor += PAGE_SIZE;
    more = candidates.length >= PAGE_SIZE;
    results.push(...await presentCandidates(type, candidates, scope, request.signal));
    if (results.length >= PAGE_SIZE || !more) break;
  }
  return { results: [...new Map(results.map(result => [result.id, result])).values()], cursor, more };
}
async function verifyPublication(request: GlobalSearchRequest, scope: Scope, results: GlobalSearchResult[], refreshProjectHints = false): Promise<GlobalSearchResult[]> {
  checkAbort(request.signal);
  const fresh = await resolveScope(request);
  if (JSON.stringify(fresh) !== JSON.stringify(scope)) throw fail();
  const projects = await readProjects(results.flatMap(item => item.projectId ? [item.projectId] : []), fresh, request.signal);
  const [nav, names] = await Promise.all([navigation(fresh, projects), refreshProjectHints ? customerNames(projects, request.signal) : Promise.resolve(new Map<string, string>())]);
  let permitted = results.filter(item => (!item.projectId || projects.some(project => project.id === item.projectId)) && permits(nav, item.type, item.projectId));
  if (refreshProjectHints) {
    // Hint labels and query matches come exclusively from this final fresh RLS read, never the cache.
    permitted = permitted.flatMap(item => {
      const project = projects.find(project => project.id === item.id);
      if (item.type !== 'project' || !project || !matchesGlobalSearchCandidate(project, request.query) || !names.has(project.client_id)) return [];
      return [{ id: project.id, type: 'project' as const, label: project.name, projectId: project.id, workspaceId: project.workspace_id,
        description: [project.project_number, names.get(project.client_id)].filter(Boolean).join(' · ') }];
    });
  }
  await actorMatches(request.actorId);
  checkAbort(request.signal);
  return permitted;
}
/** Warm metadata is an ID hint only; no cached label or permission is ever published. */
export async function warmProjectSearchCandidates(request: GlobalSearchRequest, generation: number): Promise<void> {
  const scope = await resolveScope(request);
  const nav = await navigation(scope, []);
  if (!hasItem(nav.main, ['projects'])) return;
  let query = getSupabaseClient().from('projects').select('id,name,contract_number,project_number,workspace_id').eq('archive_status', 'active');
  if (!scope.crossWorkspace) query = query.eq('workspace_id', scope.workspaceId);
  const response = await query.order('updated_at', { ascending: false }).order('id').range(0, 199).abortSignal(request.signal!);
  if (response.error) throw fail();
  await actorMatches(request.actorId);
  checkAbort(request.signal);
  const candidates = rows<Project>(response.data).filter(project => !!project.workspace_id && (scope.crossWorkspace || project.workspace_id === scope.workspaceId));
  storeGlobalSearchCandidates(request.actorId, request.workspaceId, candidates, generation);
}
/** IDs are re-read under RLS, rematched and passed through the complete existing fresh gates. */
export async function resolveProjectSearchCandidateHints(request: GlobalSearchRequest, ids: string[]): Promise<GlobalSearchResult[]> {
  if (!ids.length) return [];
  const scope = await resolveScope(request);
  const nav = await navigation(scope, []);
  if (!hasItem(nav.main, ['projects'])) return [];
  const candidates = (await readProjects([...new Set(ids)].slice(0, PAGE_SIZE), scope, request.signal))
    .filter(project => matchesGlobalSearchCandidate(project, request.query));
  const results = await presentCandidates('project', candidates, scope, request.signal);
  return verifyPublication(request, scope, results, true);
}
export async function searchGlobal(request: GlobalSearchRequest, cursors: Partial<Record<GlobalSearchType, number>> = {}, sourceTypes: GlobalSearchType[] = TYPES, carried: GlobalSearchResult[] = []): Promise<GlobalSearchPage> {
  const scope = await resolveScope(request);
  const initialNavigation = await navigation(scope, []); // No source queries when the navigation authority is unavailable.
  const permittedTypes = sourceTypes.filter(type => hasItem(initialNavigation.main, type === 'task' ? ['calendar'] : ['projects']));
  const progressive = new Map<GlobalSearchType, Awaited<ReturnType<typeof sourcePage>>>();
  let progressChain = Promise.resolve();
  const settled = await Promise.allSettled(permittedTypes.map(async type => {
    const value = await sourcePage(type, request, scope, cursors[type] || 0);
    if (request.onProgress && !carried.length) {
      progressive.set(type, value);
      const publish = progressChain.then(async () => {
        const results = await verifyPublication(request, scope, [...progressive.values()].flatMap(page => page.results));
        const partial: GlobalSearchPage = { results, cursors: { project: 0, task: 0, ...cursors }, hasMore: { project: false, task: false }, unavailable: [] };
        for (const [source, page] of progressive) { partial.cursors[source] = page.cursor; partial.hasMore[source] = page.more; }
        checkAbort(request.signal);
        request.onProgress?.(partial);
      });
      progressChain = publish.catch(() => {});
      await publish;
    }
    return value;
  }));
  checkAbort(request.signal);
  const finalScope = await resolveScope(request);
  if (JSON.stringify(finalScope) !== JSON.stringify(scope)) throw fail();
  const page: GlobalSearchPage = { results: [], cursors: { project: 0, task: 0, ...cursors }, hasMore: { project: false, task: false }, unavailable: [] };
  for (let index = 0; index < settled.length; index++) {
    const type = permittedTypes[index];
    const result = settled[index];
    if (result.status === 'rejected') { page.unavailable.push(type); continue; }
    page.results.push(...result.value.results); page.cursors[type] = result.value.cursor; page.hasMore[type] = result.value.more;
  }
  page.results = [...new Map([...(await revalidateCarried(request, finalScope, carried)), ...page.results].map(result => [`${result.type}:${result.id}`, result])).values()];
  // Fresh grants after source reads prevent a revocation during I/O from publishing stale labels.
  const projects = await readProjects(page.results.flatMap(item => item.projectId ? [item.projectId] : []), finalScope, request.signal);
  const nav = await navigation(finalScope, projects);
  page.results = page.results.filter(item => (!item.projectId || projects.some(project => project.id === item.projectId)) && permits(nav, item.type, item.projectId));
  await actorMatches(request.actorId);
  checkAbort(request.signal);
  return page;
}
export async function resolveGlobalSearchSelection(request: GlobalSearchRequest, result: GlobalSearchResult): Promise<{ url: string } | null> {
  const scope = await resolveScope(request);
  const candidates = await candidatePage(result.type, request, scope, 0, result.id);
  const { allowed, projects } = await authorizeCandidates(result.type, candidates, scope, request.signal);
  const candidate = allowed.find(item => item.id === result.id);
  if (!candidate) return null;
  const projectId = result.type === 'project' ? candidate.id : (candidate as Task).project_id;
  const project = projects.find(item => item.id === projectId);
  const names = await customerNames(projects, request.signal);
  const freshScope = await resolveScope(request);
  if (JSON.stringify(freshScope) !== JSON.stringify(scope)) return null;
  const nav = await navigation(freshScope, projects);
  if (!permits(nav, result.type, projectId)) return null;
  await actorMatches(request.actorId);
  checkAbort(request.signal);
  if (result.type === 'task') return { url: buildPrettyTaskPath({ taskTitle: (candidate as Task).title, taskId: candidate.id }) };
  const clientName = project ? names.get(project.client_id) : null;
  if (!project || !clientName) return null;
  return { url: buildPrettyProjectPath({ clientName, projectName: project.name }) };
}
